import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "./store";
import {
  setCompany, setTier,
  type TierCode, type Membership, type ActableTier, type TierRow,
} from "./actingSlice";

/**
 * Hooks for reading and changing the acting context.
 *
 * Everything a screen needs, so no component reaches into the store shape
 * directly — that keeps the slice free to change.
 */

export const useAppDispatch = () => useDispatch<AppDispatch>();

export interface ActingContextView {
  /** null for a person-tier — an operative has no company. */
  companyId: string | null;
  tier: TierCode | null;
  company: Membership | null;
  memberships: Membership[];
  /** Every active tier, for drawing the Spine modal's locked bars. */
  catalogue: TierRow[];
  /** What this caller may act as. Only these illuminate. */
  actable: ActableTier[];
  hydrated: boolean;
  /** True when the caller belongs to more than one company. */
  canSwitchCompany: boolean;
  /** True when the ACTIVE company holds more than one tier — replaces the
   *  old hybrid-only mode toggle, which was the same control with two values. */
  canSwitchTier: boolean;
  /** The person-tier case. No company, and the authority axis does not apply. */
  isPersonTier: boolean;
  /** Deployment property, from the server. Gates the demo control only. */
  demoMode: boolean;
}

export function useActingContext(): ActingContextView {
  const state = useSelector((s: RootState) => s.acting);
  const company = state.memberships.find((m) => m.company_id === state.companyId) ?? null;
  const forThisCompany = state.actable.filter((a) => a.company_id === state.companyId);

  return {
    companyId: state.companyId,
    tier: state.tier,
    company,
    memberships: state.memberships,
    catalogue: state.catalogue,
    actable: state.actable,
    hydrated: state.hydrated,
    /* Count DISTINCT companies in the grants rather than memberships: they are
       the same set today, but the grants are what the server will actually
       accept, and that is the list the switcher must not exceed. */
    canSwitchCompany:
      new Set(state.actable.filter((a) => a.company_id).map((a) => a.company_id)).size > 1,
    canSwitchTier: forThisCompany.length > 1,
    isPersonTier: state.companyId === null && state.tier !== null,
    demoMode: state.demoMode,
  };
}

/**
 * DOES THIS CALLER HOLD THIS CAPABILITY?
 *
 * For hiding a control that would 403. This is PRESENTATION, not permission —
 * the server guard runs regardless and is the only thing that decides. But a
 * button known to fail should not be drawn.
 *
 * Returns the scope too: 'own' means the control should only appear on records
 * the caller created.
 */
export function useCapability(code: string): { allowed: boolean; scope: "any" | "own" } {
  const capabilities = useSelector((s: RootState) => s.acting.capabilities);
  const hit = capabilities.find((c) => c.code === code);
  return { allowed: !!hit, scope: hit?.scope ?? "any" };
}

/**
 * WHICH SIDE OF THE MARKETPLACE AM I ON RIGHT NOW?
 *
 * Replaced `user.role` first, and now reads the TIER rather than the company's
 * kind. The distinction matters for a company holding two tiers: its `kind` is
 * a single value, but which side it is acting as changes with the switch.
 *
 *   tier1  buys labour        (was: coordinator)
 *   mep    supplies labour    (was: partner)
 *
 * The other three tiers have no position in the labour transaction at all —
 * an owner buys design and delivery, a consultant sells design, an operative
 * sells their own hours. Both flags read false for them, which is correct:
 * the screens that ask this question are not on their rails.
 */
export interface ActingSides {
  tier: TierCode | null;
  /** Buys labour — tier1. Was `isCoord`. */
  buys: boolean;
  /** Supplies labour — mep. Was `isPartner`. */
  supplies: boolean;
  hydrated: boolean;
}

export function useActingSides(): ActingSides {
  const { tier, hydrated } = useActingContext();
  return {
    tier,
    buys: tier === "tier1",
    supplies: tier === "mep",
    hydrated,
  };
}

/**
 * Change the acting company.
 *
 * The caller is responsible for what happens next — Chapter 8 Part 8 Q3
 * recommends returning to the dashboard, because a screen that exists in one
 * mode may not exist in the other.
 */
export function useSetActingCompany(): (companyId: string) => void {
  const dispatch = useAppDispatch();
  return (companyId: string) => { dispatch(setCompany(companyId)); };
}

/**
 * Switch tier — and optionally company, because the Spine modal picks a BAR,
 * and a bar is a (tier, company) pair.
 */
export function useSetActingTier(): (tier: TierCode, companyId?: string | null) => void {
  const dispatch = useAppDispatch();
  return (tier: TierCode, companyId?: string | null) => {
    dispatch(setTier({ tier, companyId }));
  };
}
